import React, { useEffect, useState } from 'react';
import ThinHeader from '../components/ThinHeader';
import { useAuth } from '../context/AuthContext';
import { me, updateMe, updateMyPassword } from '../api/authApi';
import { BATTLE_CRY_RANKS } from '../constants/battleCryRanks';

const buildEmptyBattleCries = () => ({
  single: Object.fromEntries(BATTLE_CRY_RANKS.map((rank) => [rank, ''])),
  quad: Object.fromEntries(BATTLE_CRY_RANKS.map((rank) => [rank, ''])),
});

const ProfilePage = () => {
  const { user, setUser, storeTokens } = useAuth();
  const [profileForm, setProfileForm] = useState({ displayName: '', username: '', email: '' });
  const [battleCries, setBattleCries] = useState(buildEmptyBattleCries);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await me();
        setUser(data.user);
        setProfileForm({
          displayName: data.user?.displayName || '',
          username: data.user?.username || '',
          email: data.user?.email || '',
        });
        setBattleCries({
          single: { ...buildEmptyBattleCries().single, ...(data.user?.battleCries?.single || {}) },
          quad: { ...buildEmptyBattleCries().quad, ...(data.user?.battleCries?.quad || {}) },
        });
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load profile');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [setUser]);

  const handleProfileChange = (e) => {
    setProfileForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBattleCryChange = (variant, rank, value) => {
    setBattleCries((prev) => ({
      ...prev,
      [variant]: {
        ...prev[variant],
        [rank]: value,
      },
    }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      setError('');
      setMessage('');
      const { data } = await updateMe({
        ...profileForm,
        battleCries,
      });
      setUser(data.user);
      setMessage('Profile updated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSavingPassword(true);
      setError('');
      setMessage('');
      const { data } = await updateMyPassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      storeTokens(data.accessToken, data.refreshToken, data.user);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage('Password updated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <div className="auth-page"><p>Loading profile...</p></div>;
  }

  return (
    <div className="profile-page">
      <ThinHeader
        homeTo="/"
        userLabel={user?.displayName || user?.username || user?.email || 'Player'}
        showAdminLink={user?.role === 'admin'}
      />

      <div className="stats-title-row">
        <h1>Profile & Security</h1>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="profile-success">{message}</p>}

      <section className="stats-card">
        <h2>Edit Account</h2>
        <form className="auth-form profile-form" onSubmit={saveProfile}>
          <input name="displayName" value={profileForm.displayName} onChange={handleProfileChange} placeholder="Display Name" />
          <input name="username" value={profileForm.username} onChange={handleProfileChange} placeholder="Username" />
          <input name="email" type="email" value={profileForm.email} onChange={handleProfileChange} placeholder="Email" />
          <div className="battle-cries-panel">
            <div className="battle-cries-headings">
              <h3>Personal Battle Cries</h3>
              <p>Set one line for playing a single card of each rank, and one line for playing four of that rank at once.</p>
            </div>
            <div className="battle-cries-grid battle-cries-grid--header">
              <span>Rank</span>
              <span>1 Card</span>
              <span>4 Cards</span>
            </div>
            {BATTLE_CRY_RANKS.map((rank) => (
              <div className="battle-cries-grid" key={rank}>
                <strong>{rank}</strong>
                <input
                  type="text"
                  value={battleCries.single?.[rank] || ''}
                  onChange={(e) => handleBattleCryChange('single', rank, e.target.value)}
                  placeholder={`What you shout when you play ${rank}`}
                  maxLength={120}
                />
                <input
                  type="text"
                  value={battleCries.quad?.[rank] || ''}
                  onChange={(e) => handleBattleCryChange('quad', rank, e.target.value)}
                  placeholder={`What you shout when you play four ${rank}s`}
                  maxLength={120}
                />
              </div>
            ))}
          </div>
          <button type="submit" disabled={savingProfile}>{savingProfile ? 'Saving...' : 'Save Profile'}</button>
        </form>
      </section>

      <section className="stats-card">
        <h2>Change Password</h2>
        <form className="auth-form profile-form" onSubmit={savePassword}>
          <input name="currentPassword" type="password" value={passwordForm.currentPassword} onChange={handlePasswordChange} placeholder="Current Password" />
          <input name="newPassword" type="password" value={passwordForm.newPassword} onChange={handlePasswordChange} placeholder="New Password" />
          <input name="confirmPassword" type="password" value={passwordForm.confirmPassword} onChange={handlePasswordChange} placeholder="Confirm New Password" />
          <button type="submit" disabled={savingPassword}>{savingPassword ? 'Saving...' : 'Update Password'}</button>
        </form>
      </section>
    </div>
  );
};

export default ProfilePage;
